import {isObject} from '../Util';
import Constants from './Constants';
import {REMOOV_SUPPORT_EMAIL_ADDRESS} from './Emails';
import {hostEndpoint, remoovNodeEndpoint, remoovShortDomain, reperchDomain} from './EnvVar';
import {SocialMedia} from './PickupReview';
import {PostingScope} from './Postings';
import {TLF_URL} from './Shopify';

export function toQuery(input: {[key: string]: unknown}): string {
  return Object.entries(input)
    .reduce((acc, [k, v]) => {
      acc.set(k, isObject(v) ? JSON.stringify(v) : String(v));
      return acc;
    }, new URLSearchParams())
    .toString();
}
export function parseQuery(input: string): {
  [key: string]: unknown;
} {
  return Array.from<[string, string]>(
    // @ts-ignore URLSearchParams has "entries"
    new URLSearchParams(input).entries(),
  ).reduce((acc, [k, v]) => ({...acc, [k]: v}), {});
}
export function createUrl(
  baseUrl: string,
  params: {
    [key: string]: unknown;
  },
): string {
  const query = toQuery(params);
  return query ? [baseUrl, query].join('?') : baseUrl;
}

const {
  NewSiteUrl: {FAQ_URL, HOW_URL, SERVICE_URL},
  NewSiteUrlJs: {
    YOUR_QUOTE_URL,
    PARTNERS_SIGN_UP_URL,
    PARTNERS_INVITE_URL,
    PARTNERS_RESOURCES,
    BOOKING_URL,
    PICKUP_SUBSCRIPTION_URL,
    ITEM_UPDATE_SUBSCRIPTION_URL,
    REALTOR_OFFERS_SUBSCRIPTION_URL,
    SIGN_IN_URL,
  },
  CustomerPortalUrl: {DASHBOARD_URL, ITEMS_URL, EXPIRED_ITEMS_URL},
  DashboardUrl: {
    ROOT_NAMESPACE,
    POSTINGS_URL,
    FULFILLMENTS_URL,
    PARTNERS_URL,
    ADMIN_PENDING_TASKS_URL,
    PENDING_TASKS_URL,
    ZIPCODES_URL,
  },
  Url: {
    RESCHEDULE_PICKUP_URL,
    CANCEL_PICKUP_URL,
    RESCHEDULE_IN_HOME_CONSULTATION_URL,
    CANCEL_IN_HOME_CONSULTATION_URL,
  },
} = Constants;

type NumberOrString = number | string;

export function rootUrl(params: Record<string, unknown>): string {
  return url([hostEndpoint()], params);
}
export function pickupFullUrl(id: number, params?: Record<string, unknown>): string {
  return url([remoovNodeEndpoint(), 'admin', 'pickups', String(id)], params);
}
export function itemFullUrl(id: number): string {
  return [remoovNodeEndpoint(), 'admin', 'items', String(id)].join('/');
}
export function itemIssuesFullUrl(): string {
  return [remoovNodeEndpoint(), 'admin', 'item_issues'].join('/');
}
export function customerPortalItemsFullUrl(): string {
  return [remoovNodeEndpoint(), ITEMS_URL].join('');
}
export function customerPortalDashboardFullUrl(): string {
  return [remoovNodeEndpoint(), DASHBOARD_URL].join('');
}
export function yourQuoteUrl(params?: Record<string, unknown>): string {
  return url([YOUR_QUOTE_URL.slice(1)], params);
}
export function yourQuoteFullUrl(params?: Record<string, unknown>): string {
  return url([hostEndpoint(), YOUR_QUOTE_URL.slice(1)], params);
}
export function pickupSubscriptionFullUrl(params: Record<string, unknown>): string {
  return url([hostEndpoint(), PICKUP_SUBSCRIPTION_URL.slice(1)], params);
}
export function itemUpdateSubscriptionFullUrl(params: Record<string, unknown>): string {
  return url([hostEndpoint(), ITEM_UPDATE_SUBSCRIPTION_URL.slice(1)], params);
}
export function realtorOffersSubscriptionFullUrl(params: Record<string, unknown>): string {
  return url([hostEndpoint(), REALTOR_OFFERS_SUBSCRIPTION_URL.slice(1)], params);
}
export function customerPortalExpiredItemsFullUrl(): string {
  return [remoovNodeEndpoint(), EXPIRED_ITEMS_URL].join('');
}
export function siteFaqFullUrl(): string {
  return [hostEndpoint(), FAQ_URL].join('');
}
export function siteHowFullUrl(): string {
  return [hostEndpoint(), HOW_URL].join('');
}
export function siteOurServicesFullUrl(): string {
  return [hostEndpoint(), SERVICE_URL].join('');
}
export function partnersSignupUrl(params?: Record<string, unknown>): string {
  return url([hostEndpoint(), PARTNERS_SIGN_UP_URL.slice(1)], params);
}
export function partnersInviteUrl(params: Record<string, unknown>): string {
  return url([hostEndpoint(), PARTNERS_INVITE_URL.slice(1)], params);
}
export function partnersResourcesUrl(params: Record<string, unknown>): string {
  return url([hostEndpoint(), PARTNERS_RESOURCES.slice(1)], params);
}
export function bookingUrl(params: Record<string, unknown>): string {
  return url([BOOKING_URL.slice(1)], params);
}
export function bookingFullUrl(params: Record<string, unknown>): string {
  return url([hostEndpoint(), BOOKING_URL.slice(1)], params);
}
export function reschedulePickupUrl(params): string {
  return url([hostEndpoint(), RESCHEDULE_PICKUP_URL.slice(1)], params);
}
export function cancelSchedulePickupUrl(params): string {
  return url([hostEndpoint(), CANCEL_PICKUP_URL.slice(1)], params);
}
export function rescheduleInHomeConsultationPickupUrl(params): string {
  return url([hostEndpoint(), RESCHEDULE_IN_HOME_CONSULTATION_URL.slice(1)], params);
}
export function cancelInHomeConsultationPickupUrl(params): string {
  return url([hostEndpoint(), CANCEL_IN_HOME_CONSULTATION_URL.slice(1)], params);
}
export function signInFullUrl(params): string {
  return url([hostEndpoint(), SIGN_IN_URL.slice(1)], params);
}
// Dashboard
export function showUrl(url: string): string {
  return `${url}/:id`;
}
export function newUrl(url: string): string {
  return `${url}/new`;
}
export function editUrl(url: string): string {
  return `${url}/:id/edit`;
}
export function toPostUrl(
  page?: number | null | undefined,
  item_description_id?: number | null | undefined,
  merchant_id?: number | null | undefined,
): string {
  return postingsUrl(null, page, item_description_id, merchant_id);
}
export function postedUrl(
  page?: number | null | undefined,
  item_description_id?: number | null | undefined,
  merchant_id?: number | null | undefined,
): string {
  return postingsUrl('posted', page, item_description_id, merchant_id);
}
export function toRepostUrl(
  page?: number | null | undefined,
  item_description_id?: number | null | undefined,
  merchant_id?: number | null | undefined,
): string {
  return postingsUrl('to_repost', page, item_description_id, merchant_id);
}
export function forRemovalUrl(
  page?: number | null | undefined,
  item_description_id?: number | null | undefined,
  merchant_id?: number | null | undefined,
): string {
  return postingsUrl('for_removal', page, item_description_id, merchant_id);
}
export function removedUrl(
  page?: number | null | undefined,
  item_description_id?: number | null | undefined,
  merchant_id?: number | null | undefined,
): string {
  return postingsUrl('removed', page, item_description_id, merchant_id);
}
export function newPostingUrl(): string {
  return postingsUrl('new');
}
export function showPostingUrl(id: NumberOrString): string {
  return postingsUrl(String(id));
}

export function postingsUrl(
  type: string | null | undefined,
  page?: NumberOrString | null,
  item_description_id?: NumberOrString | null,
  merchant_id?: NumberOrString | null,
  market?: NumberOrString | null,
): string {
  return url([POSTINGS_URL, type].filter(Boolean), {
    page,
    merchant_id,
    item_description_id,
    market,
  });
}
export function extPostingsUrl(tab: PostingScope, ...args: Maybe<NumberOrString>[]) {
  const type = {
    toPost: '',
    posted: 'posted',
    toRepost: 'to_repost',
    forRemoval: 'for_removal',
    removed: 'removed',
  }[tab];
  return postingsUrl(type, ...args);
}
export function fulfillmentsUrl(params?: Record<string, unknown>): string {
  return url([FULFILLMENTS_URL], params);
}
export function newFulfillmentUrl(): string {
  return url([FULFILLMENTS_URL, 'new']);
}
export function fulfillmentUrl(id: NumberOrString): string {
  return url([FULFILLMENTS_URL, id]);
}
export function fulfillmentFullUrl(id: number): string {
  return [remoovNodeEndpoint(), FULFILLMENTS_URL.slice(1), String(id)].join('/');
}
export function editFulfillmentUrl(id: NumberOrString): string {
  return url([FULFILLMENTS_URL, id, 'edit']);
}
export function photoEditItemsUrl(params?: Record<string, unknown>): string {
  return url([ROOT_NAMESPACE, 'photo_editor_queue'], params);
}
export function photoEditItemUrl(id: NumberOrString): string {
  return url([ROOT_NAMESPACE, 'photo_editor_queue', id, 'edit']);
}
export function tagItemsUrl(params?: Record<string, unknown>): string {
  return url([ROOT_NAMESPACE, 'tagger_queue'], params);
}
export function tagItemUrl(id: NumberOrString): string {
  return url([ROOT_NAMESPACE, 'tagger_queue', id, 'edit']);
}
export function merchantsUrl(params?: Record<string, unknown>): string {
  return url([ROOT_NAMESPACE, 'merchants'], params);
}
export function merchantUrl(id: NumberOrString): string {
  return url([ROOT_NAMESPACE, 'merchants', id]);
}
export function editMerchantUrl(id: NumberOrString): string {
  return url([ROOT_NAMESPACE, 'merchants', id, 'edit']);
}
export function newMerchantUrl(): string {
  return [ROOT_NAMESPACE, 'merchants', 'new'].join('/');
}
export function pickupsUrl(params?: Record<string, unknown>): string {
  return url([ROOT_NAMESPACE, 'pickups'], params);
}
export function pickupUrl(id: NumberOrString): string {
  return url([ROOT_NAMESPACE, 'pickups', id]);
}
export function zipcodesUrl(params?: Record<string, unknown>): string {
  return url([ZIPCODES_URL], params);
}
export function zipcodeUrl(zipcode: string): string {
  return url([ZIPCODES_URL, zipcode]);
}
export function newZipcodeUrl(): string {
  return url([ZIPCODES_URL, 'new']);
}
export function editZipcodeUrl(zipcode: string): string {
  return url([ZIPCODES_URL, zipcode, 'edit']);
}
export function partnersUrl(params?: Record<string, unknown>): string {
  return url([PARTNERS_URL], params);
}
export function partnerFullUrl(id: number): string {
  return urlToFullUrl(url([PARTNERS_URL, id]));
}
export function partnerUrl(id: number): string {
  return url([PARTNERS_URL, id]);
}
export function editPartnerUrl(id: number): string {
  return url([PARTNERS_URL, id, 'edit']);
}
export function newPartnerUrl(): string {
  return url([PARTNERS_URL, 'new']);
}
export function adminPendingTasksUrl(): string {
  return url([ADMIN_PENDING_TASKS_URL]);
}
export function pendingTasksUrl(): string {
  return url([PENDING_TASKS_URL]);
}
export function pickupEventUrl(pickupId: number, pickupEventId: number): string {
  return url([ROOT_NAMESPACE, 'pickups', pickupId], {pickupEventId});
}
export function pickupEventFullUrl(pickupId: number, pickupEventId: number): string {
  return urlToFullUrl(pickupEventUrl(pickupId, pickupEventId));
}
export function truckItemUrl(pickup_id: NumberOrString, truck_item_id: NumberOrString): string {
  return url([ROOT_NAMESPACE, 'pickups', pickup_id], {
    itemTab: 'truck',
    itemId: truck_item_id,
  });
}
export function truckItemFullUrl(pickup_id: number, truck_item_id: number): string {
  return urlToFullUrl(truckItemUrl(pickup_id, truck_item_id));
}
export function editPickupUrl(id: NumberOrString): string {
  return url([ROOT_NAMESPACE, 'pickups', id, 'edit']);
}
export function newPickupUrl(): string {
  return [ROOT_NAMESPACE, 'pickups', 'new'].join('/');
}
export function referralDiscountCodesUrl(params?: Record<string, unknown>): string {
  return url([ROOT_NAMESPACE, 'referral_codes'], params);
}
export function referralDiscountCodeUrl(id: number): string {
  return url([ROOT_NAMESPACE, 'referral_codes', id]);
}
export function editReferralDiscountCodeUrl(id: number): string {
  return url([ROOT_NAMESPACE, 'referral_codes', id, 'edit']);
}
export function newReferralDiscountCodeUrl(): string {
  return url([ROOT_NAMESPACE, 'referral_codes', 'new']);
}
export function ownerFullUrl(id: NumberOrString): string {
  return url([remoovNodeEndpoint(), 'admin', 'owners', String(id)]);
}
export function ownersUrl(params?: Record<string, unknown>): string {
  return url([ROOT_NAMESPACE, 'owners'], params);
}
export function ownerUrl(id: NumberOrString): string {
  return url([ROOT_NAMESPACE, 'owners', id]);
}
export function editOwnerUrl(id: NumberOrString): string {
  return url([ROOT_NAMESPACE, 'owners', id, 'edit']);
}
export function newOwnerUrl(): string {
  return [ROOT_NAMESPACE, 'owners', 'new'].join('/');
}
export function merchantOrdersUrl(params?: Record<string, unknown>): string {
  return url([ROOT_NAMESPACE, 'merchant_orders'], params);
}
export function merchantOrderFullUrl(id: NumberOrString): string {
  return urlToFullUrl(merchantOrderUrl(id));
}
export function merchantOrderUrl(id: NumberOrString): string {
  return url([ROOT_NAMESPACE, 'merchant_orders', id]);
}
export function newMerchantOrderUrl(): string {
  return url([ROOT_NAMESPACE, 'merchant_orders', 'new']);
}
export function editMerchantOrderUrl(id: NumberOrString): string {
  return url([ROOT_NAMESPACE, 'merchant_orders', id, 'edit']);
}
export function itemsUrl(params?: Record<string, unknown>): string {
  return url([ROOT_NAMESPACE, 'items'], params);
}
export function itemUrl(id: NumberOrString): string {
  return url([ROOT_NAMESPACE, 'items', id]);
}
export function itemImagesUrl(id: NumberOrString): string {
  return url([ROOT_NAMESPACE, 'items', id, 'item_images']);
}
export function editItemUrl(id: NumberOrString): string {
  return url([ROOT_NAMESPACE, 'items', id, 'edit']);
}
export function newItemUrl(): string {
  return url([ROOT_NAMESPACE, 'items', 'new']);
}
export function ownerPaymentsUrl(params?: Record<string, unknown>): string {
  return url([ROOT_NAMESPACE, 'owner_payments'], params);
}
export function ownerPaymentUrl(id: NumberOrString): string {
  return url([ROOT_NAMESPACE, 'owner_payments', id]);
}
export function ownerPaymentFullUrl(id: NumberOrString): string {
  return urlToFullUrl(ownerPaymentUrl(id));
}
export function usersUrl(params?: Record<string, unknown>): string {
  return url([ROOT_NAMESPACE, 'users'], params);
}
export function usersFreshdeskUrl(params?: Record<string, unknown>): string {
  return url([ROOT_NAMESPACE, 'users', 'freshdesk'], params);
}
export function userUrl(id: NumberOrString): string {
  return url([ROOT_NAMESPACE, 'users', id]);
}
export function userFullUrl(id: number): string {
  return [remoovNodeEndpoint(), ROOT_NAMESPACE, 'users', String(id)].join('/');
}
export function editUserUrl(id: NumberOrString): string {
  return url([ROOT_NAMESPACE, 'users', id, 'edit']);
}
export function newUserUrl(): string {
  return [ROOT_NAMESPACE, 'users', 'new'].join('/');
}
export function editCuratorCategoriesUrl(): string {
  return [ROOT_NAMESPACE, 'curator_categories'].join('/');
}
export function itemIssuesUrl(params?: Record<string, unknown>): string {
  return url([ROOT_NAMESPACE, 'item_issues'], params);
}
export function editItemIssueUrl(id: NumberOrString): string {
  return url([ROOT_NAMESPACE, 'item_issues', id, 'edit']);
}
export function newItemIssueUrl(): string {
  return url([ROOT_NAMESPACE, 'item_issues', 'new']);
}
export function itemSaleUrl(id: NumberOrString): string {
  return url([ROOT_NAMESPACE, 'item_sales', id]);
}

export function calendarUrl(timestamp?: string): string {
  return url([ROOT_NAMESPACE, 'calendar', timestamp].filter(Boolean));
}
export function listCalendarUrl(): string {
  return url([ROOT_NAMESPACE, 'calendar', 'list']);
}
export function curatorQueueUrl(params?: Record<string, unknown>): string {
  return url([ROOT_NAMESPACE, 'curator_queue'], params);
}
export function curatorQueueFullUrl(params?: Record<string, unknown>): string {
  return urlToFullUrl(curatorQueueUrl(params));
}
export function curatorFormUrl(itemId: NumberOrString): string {
  return url([ROOT_NAMESPACE, 'curator_queue', itemId, 'edit']);
}
export function curatorFormFullUrl(itemId: NumberOrString): string {
  return urlToFullUrl(curatorFormUrl(itemId));
}
export function url(
  paths: (string | number)[],
  params?: {
    [key: string]: unknown;
  },
): string {
  return [
    paths.map(String).join('/'),
    Object.entries(params || {})
      .map(([k, v]) => (v ? [k, encodeURIComponent(String(v))].join('=') : null))
      .filter(Boolean)
      .join('&'),
  ]
    .filter(Boolean)
    .join('?');
}
function urlToFullUrl(url: string): string {
  return [remoovNodeEndpoint(), url]
    .map(x => x.replace(/^\/+|\/+$/g, ''))
    .filter(Boolean)
    .join('/');
}
export function profileUrl(): string {
  return url([ROOT_NAMESPACE, 'edit_profile']);
}
export function offersUrl(params?: Record<string, unknown>): string {
  return url([ROOT_NAMESPACE, 'offers'], params);
}
export function offerUrl(id: NumberOrString): string {
  return url([ROOT_NAMESPACE, 'offers', id]);
}
export function mailToSupport(): string {
  return `mailto:${REMOOV_SUPPORT_EMAIL_ADDRESS.email}`;
}
export function donationReceiptsUrl(params?: Record<string, unknown>) {
  return url([ROOT_NAMESPACE, 'donation_receipts'], params);
}
export function reperchCartUpdateUrl(params: {id?: string; sku?: string; quantity: number}[]) {
  const stringifiedJson = encodeURIComponent(JSON.stringify(params));
  return `${TLF_URL}/cart/update?json=${stringifiedJson}`;
}
export function reperchMyCollectionFullUrl(ownerId: string, params?: Record<string, unknown>) {
  return url([reperchDomain(), 'collection', ownerId], params);
}
export function reperchUrl() {
  return `${TLF_URL}`;
}
export function getTrackUrl({url, args}: {url: string; args: Record<string, unknown>}) {
  const urlParams = toQuery({url, ...args});
  const newUrl = `${remoovNodeEndpoint()}/tracking?${urlParams}`;
  return newUrl;
}
export function getSocialMediaTrackUrl({
  socialMedia,
  encryptedId,
}: {
  socialMedia: SocialMedia;
  encryptedId: string;
}) {
  const newUrl = `${remoovShortDomain()}/${socialMedia.toLowerCase()}/${encryptedId}`;
  return newUrl;
}
